import { offHoursNotice } from './off-hours-notice.js';
import { SchedulingCredit } from './credit-slot.js';
import { SchedulingPrepaidChoice, SchedulingBalancesLink } from './prepaid-slot.js';
import { formatPhone } from '@hue/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  DatePicker,
  FormErrorSummary,
  Modal,
  Select,
  Switch,
  Textarea,
  TimePicker,
  formatCurrency,
  type CivilDate,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  clientsApi,
  getApiError,
  professionalTermsFor,
  schedulingApi,
  serviceTermsFor,
  type Appointment,
  type Client,
  type SchedulingResource,
  type ServiceOffering,
  type SoldService,
} from '../../lib/api.js';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { ClientCpfSearch } from '../../app/client-cpf-search.js';
import {
  availableSlots,
  civilMoment,
  minuteLabel,
  minutesOf,
  placeAppointments,
  placeBlocks,
  slotCheck,
} from './calendar-model.js';
import { optionList } from './scheduling-options.js';
import { useSchedulingUnits } from './scheduling-units.js';
import { DialogActions, FieldError } from './shared.js';

/// De onde o Novo agendamento parte quando nasce de um clique no calendário: o dia, e às vezes o
/// horário e o profissional da coluna clicada.
export interface AppointmentSeed {
  date?: CivilDate | undefined;
  startTime?: string | undefined;
  professionalId?: string | undefined;
  /// Serviço já vendido: cliente e serviço vêm da venda, e o pedido não pede sinal (ADR 0046).
  sold?: SoldService | undefined;
}

const appointmentFormSchema = z.object({
  /// Só a remarcação escolhe: o atendimento pode ir para outra unidade (ADR 0039).
  prepaidBalanceId: z.string(),
  businessUnitId: z.string(),
  clientId: z.string().min(1, 'Escolha o cliente.'),
  serviceId: z.string().min(1, 'Escolha o serviço.'),
  professionalId: z.string().min(1, 'Selecione uma opção válida.'),
  assistantId: z.string(),
  roomId: z.string(),
  date: z.string().min(1, 'Escolha a data.'),
  startTime: z.string().min(1, 'Informe o horário.'),
});
type AppointmentForm = z.infer<typeof appointmentFormSchema>;

/// Quantos clientes a busca traz por vez. Cliente não tem teto, então a lista nunca vem inteira.
const CLIENT_PAGE = 20;
const SEARCH_DELAY_MS = 250;

export function AppointmentDialog({
  open,
  onOpenChange,
  appointment,
  seed,
  loading = false,
  error,
  today,
  timeZone,
  services,
  professionals,
  rooms,
  readOnly = false,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment | null;
  seed?: AppointmentSeed | undefined;
  loading?: boolean;
  error?: string | undefined;
  today: CivilDate;
  timeZone: string;
  services: ServiceOffering[];
  professionals: SchedulingResource[];
  rooms: SchedulingResource[];
  readOnly?: boolean;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const sold = appointment ? undefined : seed?.sold;
  const [requestError, setRequestError] = useState<string>();
  const [clientName, setClientName] = useState('');
  const form = useForm<AppointmentForm>({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: emptyAppointment(today, seed),
  });
  /// `useWatch` promete um formulário pela metade, mas este nunca está: `defaultValues` preenche
  /// todo campo no primeiro render e nenhum controle devolve `undefined`.
  const values = useWatch({ control: form.control }) as AppointmentForm;
  useEffect(() => {
    if (!open) return;
    form.reset(
      appointment ? appointmentFormOf(appointment, timeZone) : emptyAppointment(today, seed),
    );
  }, [appointment, form, open, seed, timeZone, today]);
  // O nome do cliente acompanha o diálogo que abre, ajustado no render para não pintar uma vez com
  // o nome do agendamento anterior.
  const syncKey = open ? (appointment?.id ?? 'new') : null;
  const [syncedKey, setSyncedKey] = useState<string | null>(null);
  if (syncKey !== syncedKey) {
    setSyncedKey(syncKey);
    setClientName(appointment?.clientDisplayName ?? seed?.sold?.clientDisplayName ?? '');
  }

  const disabled =
    readOnly ||
    appointment?.status === 'CANCELLED' ||
    appointment?.status === 'COMPLETED' ||
    appointment?.status === 'NO_SHOW';
  const { units, activeBusinessUnitId, commissionsEnabled } = useSchedulingUnits();
  /// A unidade de destino, quando a remarcação leva o atendimento para outra. Aí os recursos e os
  /// horários livres são os de lá, não os da agenda aberta.
  const targetUnit =
    appointment && values.businessUnitId && values.businessUnitId !== appointment.businessUnitId
      ? values.businessUnitId
      : undefined;
  const targetResources = useQuery({
    queryKey: ['company', 'scheduling', 'resources', { businessUnitId: targetUnit }],
    queryFn: () =>
      schedulingApi.listResources({
        businessUnitId: targetUnit,
        activity: 'ACTIVE',
        kind: 'ALL',
        sort: 'displayName',
        direction: 'asc',
      }),
    enabled: open && Boolean(targetUnit),
    retry: false,
  });
  const unitProfessionals = targetUnit
    ? (targetResources.data ?? []).filter(
        (item) => item.kind === 'PROFESSIONAL' && item.attendsSchedule !== false,
      )
    : professionals;
  const unitRooms = targetUnit
    ? (targetResources.data ?? []).filter((item) => item.kind === 'ROOM')
    : rooms;
  const serviceId = appointment?.serviceId ?? values.serviceId;
  // Só quem realiza o serviço é oferecido (ADR 0074). Quem já atende este horário continua na
  // lista, porque o que foi marcado antes de a empresa mudar o cadastro segue valendo.
  const performers = unitProfessionals.filter(
    (item) =>
      !serviceId || item.serviceIds.includes(serviceId) || item.id === appointment?.professionalId,
  );
  const selectedService = services.find((service) => service.id === values.serviceId);
  /// Preço e duração do serviço na unidade em que o horário é marcado (ADR 0074).
  const termsOf = (service: ServiceOffering) => serviceTermsFor(service, activeBusinessUnitId);
  const bookedService = services.find((service) => service.id === serviceId);
  const unitTerms = bookedService
    ? serviceTermsFor(
        bookedService,
        appointment ? (targetUnit ?? appointment.businessUnitId) : activeBusinessUnitId,
      )
    : undefined;
  /// E os do profissional escolhido sobre eles; com qualquer profissional, os da unidade.
  const chosen =
    values.professionalId === 'ANY'
      ? undefined
      : performers.find((item) => item.id === values.professionalId);
  const chosenTerms = unitTerms ? professionalTermsFor(chosen, serviceId, unitTerms) : undefined;
  const termsVary =
    unitTerms !== undefined &&
    new Set(
      performers.map((item) => {
        const terms = professionalTermsFor(item, serviceId, unitTerms);
        return `${terms.durationMinutes}:${terms.priceCents}`;
      }),
    ).size > 1;
  /// Trocar de profissional sugere o preço e a duração dele; só valem se a pessoa aceitar
  /// (ADR 0074, decisão 13).
  const [acceptedFor, setAcceptedFor] = useState<string>();
  const suggestion =
    appointment &&
    chosen &&
    chosenTerms &&
    chosen.id !== appointment.professionalId &&
    (chosenTerms.durationMinutes !== appointment.durationMinutes ||
      chosenTerms.priceCents !== appointment.priceCents)
      ? { professional: chosen, terms: chosenTerms }
      : undefined;
  const applyTerms = suggestion !== undefined && acceptedFor === suggestion.professional.id;
  /// Remarcar preserva a duração real, que pode ter sido esticada (ADR 0034); agendar usa a do
  /// serviço escolhido, ou a do profissional quando a pessoa aceita a dele.
  const durationMinutes = appointment
    ? applyTerms
      ? suggestion.terms.durationMinutes
      : Math.round((Date.parse(appointment.endsAt) - Date.parse(appointment.startsAt)) / 60_000)
    : selectedService
      ? chosenTerms?.durationMinutes
      : undefined;
  const day = useQuery({
    queryKey: [
      'company',
      'scheduling',
      'calendar',
      { businessUnitId: targetUnit, from: values.date, to: values.date, includeCancelled: false },
    ],
    queryFn: () =>
      schedulingApi.getCalendar({
        businessUnitId: targetUnit,
        from: values.date,
        to: values.date,
        includeCancelled: false,
      }),
    enabled: open && !disabled && Boolean(values.date),
    retry: false,
  });
  const schedule = {
    resources: [...performers, ...unitRooms],
    appointments: placeAppointments(day.data?.appointments ?? [], timeZone),
    blocks: placeBlocks(day.data?.blocks ?? [], timeZone),
  };
  const slots = values.date
    ? availableSlots(schedule, {
        appointmentId: appointment?.id,
        professionalId: values.professionalId,
        roomId: values.roomId || null,
        date: values.date,
        durationMinutes: durationMinutes ?? 15,
      })
    : undefined;
  const professional =
    values.professionalId === 'ANY'
      ? { id: 'ANY', displayName: 'os profissionais disponíveis' }
      : performers.find((item) => item.id === values.professionalId);
  const room = unitRooms.find((item) => item.id === values.roomId);
  const { problem, warning } =
    day.data && professional && values.startTime && durationMinutes
      ? slotCheck(schedule, {
          appointmentId: appointment?.id,
          professionalId: professional.id,
          roomId: values.roomId || null,
          date: values.date,
          start: minutesOf(values.startTime),
          end: minutesOf(values.startTime) + durationMinutes,
        })
      : { problem: '', warning: '' };
  const timeError =
    form.formState.errors.startTime?.message ??
    (problem ? busyNotice(values.startTime, problem, professional, room) : undefined);

  /// O diálogo só se fecha por aqui — Voltar, Esc, clique fora —, e fechar esquece a recusa.
  function close() {
    setRequestError(undefined);
    onOpenChange(false);
  }

  async function save(data: AppointmentForm) {
    setRequestError(undefined);
    // A prévia já disse por que não cabe, junto ao horário; mandar à API só repetiria a recusa.
    if (problem) return;
    try {
      if (appointment) {
        await schedulingApi.rescheduleAppointment(appointment.id, {
          ...(targetUnit ? { businessUnitId: targetUnit } : {}),
          professionalId: data.professionalId,
          ...(commissionsEnabled ? { assistantId: data.assistantId || null } : {}),
          roomId: data.roomId || null,
          date: data.date,
          startTime: data.startTime,
          ...(applyTerms
            ? { applyProfessionalTerms: true, durationMinutes: suggestion.terms.durationMinutes }
            : {}),
        });
      } else {
        await schedulingApi.createAppointment({
          ...(data.prepaidBalanceId ? { prepaidBalanceId: data.prepaidBalanceId } : {}),
          ...(sold ? { saleItemUnitId: sold.saleItemUnitId } : {}),
          clientId: data.clientId,
          serviceId: data.serviceId,
          professionalId: data.professionalId,
          ...(commissionsEnabled ? { assistantId: data.assistantId || null } : {}),
          roomId: data.roomId || null,
          date: data.date,
          startTime: data.startTime,
        });
      }
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const price = appointment
    ? formatCurrency(
        applyTerms ? suggestion.terms.priceCents : appointment.priceCents,
        appointment.currency,
      )
    : sold
      ? formatCurrency(sold.priceCents, sold.currency)
      : selectedService && chosenTerms
        ? formatCurrency(chosenTerms.priceCents, selectedService.currency)
        : null;
  const summary = [
    values.startTime && durationMinutes
      ? `Das ${values.startTime} às ${minuteLabel(minutesOf(values.startTime) + durationMinutes)}`
      : null,
    durationMinutes ? `${durationMinutes} min` : null,
    price,
  ]
    .filter(Boolean)
    .join(' · ');
  /// Carregando ou com falha ainda não há agendamento em mãos, mas o diálogo já é o de remarcar:
  /// sem isto a ficha aberta pelo endereço se anuncia como "Novo agendamento" até o dado chegar.
  const rescheduling = Boolean(appointment) || loading || Boolean(error);
  const showForm = !loading && !error;
  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={rescheduling ? 'Remarcar agendamento' : 'Novo agendamento'}
      description={
        rescheduling
          ? 'A duração, o preço e os nomes usados ficam preservados no histórico.'
          : 'Solicitar não reserva o horário. Somente a confirmação da unidade garante o atendimento.'
      }
      {...(showForm && !disabled
        ? {
            footer: (
              <>
                <FormErrorSummary form={formId} className="basis-full" />
                <span className="mr-auto text-xs tabular-nums text-content-secondary">
                  {summary}
                </span>
                <Button type="button" variant="outline" onClick={close}>
                  Voltar
                </Button>
                <Button type="submit" form={formId} loading={form.formState.isSubmitting}>
                  {appointment ? 'Remarcar' : 'Agendar'}
                </Button>
              </>
            ),
          }
        : {})}
    >
      {loading ? (
        <LoadingCard label="Carregando agendamento" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form id={formId} noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ClientCpfSearch
                disabled={Boolean(appointment) || Boolean(sold?.clientId) || disabled}
                onSelect={(clientId, name) => {
                  setClientName(name);
                  form.setValue('prepaidBalanceId', '');
                  form.setValue('clientId', clientId, { shouldValidate: true });
                }}
              />
            </div>
            <ClientField
              className="sm:col-span-2"
              selectedLabel={clientName}
              disabled={Boolean(appointment) || Boolean(sold?.clientId) || disabled}
              {...(appointment ? { hint: 'Remarcar mantém o cliente e o serviço.' } : {})}
              error={form.formState.errors.clientId?.message}
              onSelect={(clientId, name) => {
                setClientName(name);
                form.setValue('prepaidBalanceId', '');
                form.setValue('clientId', clientId, { shouldValidate: true });
              }}
            />
            <div className="sm:col-span-2">
              <SchedulingCredit
                clientId={values.clientId}
                businessUnitId={values.businessUnitId || undefined}
                prepaid={Boolean(values.prepaidBalanceId)}
              />
            </div>
            <Combobox
              className="sm:col-span-2"
              label="Serviço"
              placeholder="Buscar serviço"
              selectedLabel={
                selectedService?.displayName ??
                appointment?.serviceDisplayName ??
                sold?.serviceDisplayName ??
                ''
              }
              options={services.map((service) => ({
                value: service.id,
                label: service.displayName,
                description: `${termsOf(service).durationMinutes} min · ${formatCurrency(termsOf(service).priceCents, service.currency)}`,
              }))}
              // Sem texto livre, toda escolha é uma opção da lista.
              onSelect={(choice) => {
                const next = (choice as { value: string }).value;
                form.setValue('prepaidBalanceId', '');
                form.setValue('serviceId', next, { shouldValidate: true });
                if (
                  values.professionalId !== 'ANY' &&
                  !unitProfessionals.some(
                    (item) => item.id === values.professionalId && item.serviceIds.includes(next),
                  )
                )
                  form.setValue('professionalId', 'ANY');
              }}
              onClear={() => form.setValue('serviceId', '')}
              emptyLabel="Nenhum serviço encontrado"
              disabled={Boolean(appointment) || Boolean(sold) || disabled}
              error={form.formState.errors.serviceId?.message}
            />
            {sold ? (
              <p className="sm:col-span-2 text-sm">
                Serviço já pago na venda {sold.saleNumber}: o pedido não pede sinal, e o pagamento
                continua naquela venda.
              </p>
            ) : !appointment ? (
              <div className="sm:col-span-2">
                <SchedulingPrepaidChoice
                  key={`${values.clientId}:${values.serviceId}`}
                  clientId={values.clientId}
                  clientName={clientName}
                  date={values.date}
                  serviceId={values.serviceId}
                  value={values.prepaidBalanceId}
                  onChange={(id) => form.setValue('prepaidBalanceId', id)}
                />
              </div>
            ) : appointment.prepaidBalanceId ? (
              <p className="sm:col-span-2 text-sm">
                Este atendimento tem uma sessão reservada do pacote.
                <span className="mt-2 block">
                  <SchedulingBalancesLink clientId={appointment.clientId} />
                </span>
              </p>
            ) : null}
            {appointment && units.length > 1 ? (
              <div className="sm:col-span-2">
                <Select
                  label="Unidade"
                  value={values.businessUnitId}
                  options={units.map((unit) => ({ value: unit.id, label: unit.displayName }))}
                  onValueChange={(value) => {
                    // Profissional e sala são de cada unidade: trocar de unidade pede escolher de novo.
                    form.setValue('businessUnitId', value);
                    form.setValue('professionalId', 'ANY');
                    form.setValue('roomId', 'ANY');
                  }}
                  disabled={disabled}
                />
                <p className="mt-1.5 text-xs text-content-faint">
                  Trocar de unidade leva o atendimento para a agenda dela.
                </p>
              </div>
            ) : null}
            <div>
              <Select
                label="Profissional"
                value={values.professionalId}
                options={[
                  { value: 'ANY', label: 'Qualquer profissional disponível' },
                  ...performers.map((item) => ({ value: item.id, label: item.displayName })),
                ]}
                onValueChange={(value) =>
                  form.setValue('professionalId', value, { shouldValidate: true })
                }
                disabled={disabled}
              />
              <FieldError message={form.formState.errors.professionalId?.message} />
              {commissionsEnabled ? (
                <Select
                  label="Assistente"
                  value={values.assistantId}
                  options={[
                    { value: '', label: 'Sem assistente' },
                    ...performers
                      .filter(
                        (item) =>
                          item.id !== values.professionalId && item.receivesCommission === true,
                      )
                      .map((item) => ({ value: item.id, label: item.displayName })),
                  ]}
                  onValueChange={(value) => form.setValue('assistantId', value)}
                  disabled={disabled}
                />
              ) : null}
              {serviceId &&
              performers.length === 0 &&
              (!targetUnit || targetResources.isSuccess) ? (
                <p role="status" className="mt-1.5 text-xs font-medium text-warning">
                  Nenhum profissional desta unidade realiza este serviço. Marque o serviço no
                  cadastro do profissional, em Profissionais.
                </p>
              ) : values.professionalId === 'ANY' ? (
                <p className="mt-1.5 text-xs text-content-faint">
                  A unidade aloca um profissional que realiza o serviço e revalida a escolha ao
                  confirmar.
                  {termsVary && !appointment
                    ? ' Preço e duração seguem os do profissional alocado.'
                    : ''}
                </p>
              ) : null}
              {suggestion ? (
                <div className="mt-3 grid gap-1.5">
                  <Switch
                    checked={applyTerms}
                    onChange={(checked) =>
                      setAcceptedFor(checked ? suggestion.professional.id : undefined)
                    }
                    disabled={disabled}
                  >
                    {`Usar o preço e a duração de ${suggestion.professional.displayName}: ${formatCurrency(
                      suggestion.terms.priceCents,
                      appointment!.currency,
                    )} · ${suggestion.terms.durationMinutes} min`}
                  </Switch>
                  <p className="text-xs text-content-faint">
                    Desligado, o atendimento fica com o preço e a duração atuais. Com sinal pago ou
                    venda vinculada, o preço continua o atual.
                  </p>
                </div>
              ) : null}
            </div>
            <Select
              label="Sala"
              value={values.roomId}
              options={[
                { value: 'ANY', label: 'Qualquer sala disponível' },
                ...optionList(unitRooms, 'Sem sala'),
              ]}
              onValueChange={(value) => form.setValue('roomId', value)}
              disabled={disabled}
            />
            <DatePicker
              label="Data"
              value={values.date || null}
              onValueChange={(value) =>
                form.setValue('date', value ?? '', { shouldValidate: true })
              }
              today={today}
              error={form.formState.errors.date?.message}
              disabled={disabled}
            />
            <TimePicker
              label="Horário inicial"
              value={values.startTime || null}
              onValueChange={(value) =>
                form.setValue('startTime', value ?? '', { shouldValidate: true })
              }
              slots={slots}
              error={timeError}
              hint="Digite, por exemplo, 1430 ou 14h30, ou escolha no relógio."
              disabled={disabled}
            />
          </div>
          {/* Fora do expediente é exceção legítima: avisa e deixa agendar (ADR 0034, decisão 9). */}
          {warning && !problem ? (
            <p role="status" className="mt-3 text-xs font-medium text-warning">
              {offHoursNotice(values.startTime, professional!, warning)}
            </p>
          ) : null}
          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      )}
    </Modal>
  );
}

/// Cliente escolhido por busca na API (ADR 0034): poucos por vez, por nome, telefone ou documento,
/// com o nome ignorando acentos. A lista inteira nunca é carregada.
function ClientField({
  selectedLabel,
  disabled,
  error,
  hint,
  className,
  onSelect,
}: {
  selectedLabel: string;
  disabled: boolean;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string | undefined;
  onSelect: (clientId: string, displayName: string) => void;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const result = useQuery({
    queryKey: ['company', 'scheduling', 'client-search', query],
    queryFn: () =>
      clientsApi.list({
        ...(query ? { search: query } : {}),
        activity: 'ACTIVE',
        kind: 'ALL',
        access: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: CLIENT_PAGE,
        offset: 0,
      }),
    enabled: !disabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const clients = result.data?.clients ?? [];
  const total = result.data?.total ?? 0;
  const status = !result.data
    ? undefined
    : total > clients.length
      ? `${clients.length} de ${total} clientes${query ? '' : ' · digite para filtrar'}`
      : `${total} ${total === 1 ? 'cliente' : 'clientes'}`;
  return (
    <Combobox
      className={className}
      label="Cliente"
      placeholder="Buscar por nome, telefone ou documento"
      selectedLabel={selectedLabel}
      options={clients.map((client) => ({
        value: client.id,
        label: client.displayName,
        description: describeClient(client),
      }))}
      // Sem texto livre, toda escolha é um dos clientes que a busca acabou de trazer.
      onSelect={(choice) => {
        const { value } = choice as { value: string };
        onSelect(value, clients.find((item) => item.id === value)!.displayName);
      }}
      onQueryChange={setText}
      loading={result.isFetching}
      status={status}
      onClear={() => onSelect('', '')}
      emptyLabel={text.trim() ? 'Nenhum cliente encontrado' : 'Digite o nome ou o CPF do cliente'}
      disabled={disabled}
      error={error}
      hint={hint}
    />
  );
}

function describeClient(client: Client): string | undefined {
  const parts = [formatPhone(client.phone), client.document].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function CancellationDialog({
  appointment,
  onClose,
  onSaved,
}: {
  appointment: Appointment;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (reason.trim().length < 3) {
      setError('Informe o motivo do cancelamento.');
      return;
    }
    setSaving(true);
    try {
      await schedulingApi.cancelAppointment(appointment.id, reason);
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Cancelar agendamento"
      footer={
        <DialogActions
          label="Cancelar agendamento"
          onCancel={onClose}
          loading={saving}
          onSubmit={() => void submit()}
        />
      }
      description="O horário será liberado. Se o atendimento tiver venda, sai dela só o serviço: produtos e pacotes continuam vendidos, e o que foi recebido acima do novo total vira crédito do cliente. Se o serviço era o único item, a venda é cancelada. O histórico será preservado."
    >
      <p className="mb-4 text-sm">
        Cancelar o atendimento de <strong>{appointment.clientDisplayName}</strong>?
      </p>
      <Textarea
        label="Motivo do cancelamento"
        rows={4}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      {error ? <RequestAlert message={error} /> : null}
    </Modal>
  );
}

function emptyAppointment(today: CivilDate, seed?: AppointmentSeed): AppointmentForm {
  return {
    prepaidBalanceId: '',
    businessUnitId: '',
    clientId: seed?.sold?.clientId ?? '',
    serviceId: seed?.sold?.serviceId ?? '',
    professionalId: 'ANY',
    assistantId: '',
    roomId: 'ANY',
    date: seed?.date ?? today,
    startTime: seed?.startTime ?? '',
  };
}

function appointmentFormOf(appointment: Appointment, timeZone: string): AppointmentForm {
  const start = civilMoment(appointment.startsAt, timeZone);
  return {
    prepaidBalanceId: appointment.prepaidBalanceId ?? '',
    businessUnitId: appointment.businessUnitId,
    clientId: appointment.clientId,
    serviceId: appointment.serviceId,
    professionalId: appointment.anyProfessional ? 'ANY' : appointment.professionalId,
    assistantId: appointment.assistantId ?? '',
    roomId: appointment.anyRoom ? 'ANY' : (appointment.roomId ?? ''),
    date: start.date,
    startTime: minuteLabel(start.minute),
  };
}

/// A recusa nomeia quem está indisponível: a sala, quando é ela que está bloqueada ou ocupada.
function busyNotice(
  time: string,
  problem: string,
  professional: { displayName: string } | undefined,
  room: { displayName: string } | undefined,
): string {
  const ofRoom = problem.startsWith('sala ') && room;
  const who = ofRoom ? room.displayName : professional?.displayName;
  const reason = ofRoom ? (problem === 'sala bloqueada' ? 'bloqueada' : 'ocupada') : problem;
  return `${time} não está livre para ${who}: ${reason}.`;
}
